// A faithful re-implementation of the backend's automatic assignment, used to
// show admins what the settings actually do.
//
// THIS FILE MIRRORS THE BACKEND. Every rule below has a counterpart in
// celer-backend, and the whole point of the simulator is that the two agree —
// an example that does not match reality is worse than no example at all. When
// you change one side, change the other:
//
//   util/auto-assign-order.js                  -> getFreeDeliveries, rankForOrder
//   controllers/shop/order/order-assign.js     -> runSweep, the confirm timeout
//   controllers/shop/order/order-notify.js     -> when an order asks for a check
//   controllers/shop/order/order-state.js      -> SWEEP_INTERVAL_SECONDS,
//                                                 SWEEP_DEBOUNCE_SECONDS
//   util/auto-assign-score.js                  -> confirmWindowSeconds,
//                                                 resolveResendCount,
//                                                 resolveRevokeWindowSeconds
//
// There is exactly ONE way an order is assigned automatically: the periodic
// check. The old per-order triggers (on creation, on ready) are gone from both
// sides — creation and readiness now only ASK for a check, five seconds out.
//
// What is deliberately NOT modelled: Wolt Drive (a separate mechanism that
// never reaches this code), order completion (nothing here finishes a delivery,
// so a driver's "last completed order" only ages, it never resets), and
// distance — because the real system does not use it either. That last one is
// not an omission, it is the single most surprising thing about auto-assign and
// the simulator says so out loud.

// controllers/shop/order/order-state.js — how long a city may go without being
// looked at when nothing at all happens.
export const SWEEP_INTERVAL_SECONDS = 30;
// ...and how quickly a check follows something that made an order assignable.
// Debounced, so a burst collapses into one check.
export const SWEEP_DEBOUNCE_SECONDS = 5;
// Why a driver was not given an order. The order of the checks matters: it is
// the order the backend applies them in, and the first one that fails is the
// one the admin is shown.
import {
	computeBatchFit,
	computeEffectiveLoad,
	computeRouteCost,
	computeSupplyScarcity,
	confirmWindowSeconds,
	orderUrgency,
	RESEND_INTERVAL_SECONDS,
	resolveDistanceForScoring,
	resolveResendCount,
	resolveRevokeWindowSeconds,
	resolveScoring,
	scoreCandidate,
	supplyWeightFactors,
} from './autoAssignScoring.js';

// controllers/shop/order/order-assign.js — how long an assignment waits for its
// rider. No longer a constant on either side: it follows the city's resend
// setting, so it is re-exported here rather than duplicated.
export {
	confirmWindowSeconds,
	resolveResendCount,
	resolveRevokeWindowSeconds,
	RESEND_INTERVAL_SECONDS,
};

// What every city ran before the resends became configurable, and still the
// floor. Kept for the tests and the copy that talks about the default city.
export const CONFIRM_TIMEOUT_SECONDS = confirmWindowSeconds({});

export const SKIP_REASONS = {
	BLOCKED: 'blocked',
	NO_PUSH_TOKEN: 'noPushToken',
	// The simulator never produces this one either — its drivers are invented, so
	// none of them is on a real city's excluded list — but the live decision
	// board reads the same vocabulary and would render an empty red line without
	// it.
	EXCLUDED_FROM_AUTO_ASSIGN: 'excludedFromAutoAssign',
	// Live board only (the simulator has no piece-work jobs): the rider holds an
	// envelope / newspaper job, so the check gives them nothing until it is done.
	ON_PIECE_WORK: 'onPieceWork',
	ALREADY_IGNORED: 'alreadyIgnored',
	// The simulator never produces this one — it has no admin actions — but the
	// live decision board reads the same vocabulary, so the mirror has to carry
	// it or a real verdict renders with no words.
	REMOVED_BY_ADMIN: 'removedByAdmin',
	TOO_MANY_ORDERS: 'tooManyOrders',
	// Below the limit, but reached it earlier and has not been empty since.
	HOLDING_UNTIL_EMPTY: 'holdingUntilEmpty',
	TOO_SOON: 'tooSoon',
	NOT_CLOCKED_IN: 'notClockedIn',
	NOT_ALLOWED_BY_SHOP: 'notAllowedByShop',
	TOO_FAR: 'tooFar',
	BATCH_FULL: 'batchFull',
	NO_BATCH_TO_JOIN: 'noBatchToJoin',
	SCORE_TOO_LOW: 'scoreTooLow',
	TAKEN_BY_EARLIER_ORDER: 'takenByEarlierOrder',
};

// Why an order was not looked at by the sweep at all.
export const ORDER_SKIP_REASONS = {
	NOT_OPERATING: 'notOperating',
	ALREADY_ASSIGNED: 'alreadyAssigned',
	NOT_READY: 'notReady',
	NOT_PENDING_LONG_ENOUGH: 'notPendingLongEnough',
	NO_FREE_DRIVER: 'noFreeDriver',
};

// util/funcs.js — checkIsAllowedDelivery. An unset list means every driver.
const isAllowedByShop = (shop, driverId) => {
	if (!shop?.allowedDeliveryIds || shop.allowedDeliveryIds === 'all') {
		return true;
	}
	if (!Array.isArray(shop.allowedDeliveryIds)) {
		return true;
	}
	return shop.allowedDeliveryIds.some(id => `${id}` === `${driverId}`);
};

// The backend stores these three as strings and treats "0" (and anything falsy)
// as "no limit". Number('') is 0, so the empty input an admin can leave behind
// has to read as off, exactly like the server's `!value || value != "0"` pair.
//
// Exported for the parity sweep: the backend's counterpart is resolveLimits in
// util/auto-assign-order.js, and the two deciding differently about an empty or
// malformed value means the simulator shows a limit the server is not applying.
export const limitOf = value => {
	if (value === null || value === undefined || value === '' || value == '0') {
		return null;
	}
	const n = Number(value);
	return isFinite(n) ? n : null;
};

// util/get-order-preparation-status.js
const readyAtOf = order => order.createdAtSeconds + (Number(order.delayMinutes) || 0) * 60;

// util/auto-assign-order.js — toBatchOrder. What the batching term reads about
// one order: where it is collected, where it goes, when it is ready, and whether
// the rider already has it in the bag.
const toBatchOrder = (order, shopsById, carrying) => {
	const shop = shopsById[order.shopId];
	return {
		id: order.id,
		carrying: !!carrying,
		shop: shop ? { lat: shop.lat, lng: shop.lng } : null,
		dest: { lat: order.lat, lng: order.lng },
		// Seconds here rather than ms — the simulation's own clock. computeBatchFit
		// divides the difference by 60000, so the values are scaled to match.
		readyAt: readyAtOf(order) * 1000,
	};
};

// The backend asks `Date.now() > readyAt`, and on a millisecond clock a check
// running in the ready second is always some fraction past it — so it sees a
// ready order. Written as `>` here it would not, because this clock only counts
// whole seconds, and an order that raises its ready event at exactly readyAt
// would then not be ready to the check running in that same second. `>=` is
// what the millisecond version actually does.
export const isOrderReady = (order, atSeconds) => atSeconds >= readyAtOf(order);

// How long ago this driver completed their last order, in seconds, at a given
// moment. null means they have never completed one here — the backend's
// `lastOrder` being null, which skips the minutes check entirely and sorts them
// to the very front (it stores new Date(0) for them).
const secondsSinceLastCompleted = (driver, atSeconds, state) => {
	// A delivery finished DURING this run resets the clock, exactly as a real
	// completion does. Only scenarios that opt into completions ever set this;
	// without it the driver's starting figure just ages, which is what every
	// hand-built scenario in the suite relies on.
	const completedAt = state?.lastCompletedAtSeconds?.[driver.id];
	if (completedAt !== undefined && completedAt !== null) {
		return atSeconds - completedAt;
	}

	if (driver.minutesSinceLastCompleted === null || driver.minutesSinceLastCompleted === '') {
		return null;
	}
	const base = Number(driver.minutesSinceLastCompleted);
	if (!isFinite(base)) {
		return null;
	}
	return base * 60 + atSeconds;
};

// util/auto-assign-order.js — updateRiderCapLatch, on the run's state instead of
// the server's process memory.
//
// Returns whether the driver is HELD, which is only ever interesting below the
// limit: at or above it the ordinary limit is what excludes them, and reporting
// the latch there would hide the plainer reason.
const updateCapLatch = ({ state, driverId, liveOrders, minFreeOrders, enabled }) => {
	if (!state.atCapDriverIds) {
		state.atCapDriverIds = new Set();
	}

	if (!enabled || minFreeOrders === null) {
		state.atCapDriverIds.delete(driverId);
		return false;
	}

	if (liveOrders >= minFreeOrders) {
		state.atCapDriverIds.add(driverId);
		return false;
	}

	if (liveOrders === 0) {
		state.atCapDriverIds.delete(driverId);
		return false;
	}

	return state.atCapDriverIds.has(driverId);
};

// util/auto-assign-order.js — getFreeDeliveries.
//
// Returns every driver with a verdict, not just the eligible ones: the rejected
// ones with their reason are the entire value of this screen. The eligible ones
// come back in the backend's order — longest without a completed order first.
export const rankDrivers = ({ drivers, state, settings, atSeconds, excludeDriverIds = [] }) => {
	const minFreeOrders = limitOf(settings.orderAutoAssignMinFreeOrders);
	// SECONDS, despite the field name — see auto-assign-order.js.
	const minFreeSeconds = limitOf(settings.orderAutoAssignMinFreeMinutes);

	const verdicts = drivers.map(driver => {
		const liveOrders = state.liveOrdersByDriver[driver.id] ?? 0;
		const idleSeconds = secondsSinceLastCompleted(driver, atSeconds, state);

		const base = {
			driverId: driver.id,
			liveOrders,
			idleSeconds,
		};

		// The backend really does filter on this, so the rule stays here. The
		// simulator just never sets it: a blocked driver is not part of any
		// question worth asking, and a toggle for it was only noise.
		if (driver.blocked) {
			return { ...base, eligible: false, reason: SKIP_REASONS.BLOCKED };
		}

		if (!driver.hasPushToken) {
			return { ...base, eligible: false, reason: SKIP_REASONS.NO_PUSH_TOKEN };
		}

		// The latch behind holdRiderUntilEmpty. The backend keeps this in process
		// memory across sweeps (ridersAtCap in util/auto-assign-order.js); here it
		// lives on the run's own state, which is the same thing for a scenario
		// that starts from nothing.
		//
		// Read for every driver, at or below the limit, because reaching the
		// ceiling is what sets it and emptying is what clears it.
		const held = updateCapLatch({
			state,
			driverId: driver.id,
			liveOrders,
			minFreeOrders,
			enabled: settings.autoAssignScoring?.holdRiderUntilEmpty === true,
		});

		if (minFreeOrders !== null && liveOrders >= minFreeOrders) {
			return { ...base, eligible: false, reason: SKIP_REASONS.TOO_MANY_ORDERS };
		}

		if (held) {
			return { ...base, eligible: false, reason: SKIP_REASONS.HOLDING_UNTIL_EMPTY };
		}

		// Mirrors `if (lastOrder) { ... }`: a driver with no completed order is
		// never held back by this, however long the limit is.
		if (minFreeSeconds !== null && idleSeconds !== null && idleSeconds <= minFreeSeconds) {
			return { ...base, eligible: false, reason: SKIP_REASONS.TOO_SOON };
		}

		// An unset clock reads as "not clocked in": the backend compares the last
		// event against 'ci' and 'bo', and undefined matches neither.
		if (settings.withClock && driver.clockEvent !== 'ci' && driver.clockEvent !== 'bo') {
			return { ...base, eligible: false, reason: SKIP_REASONS.NOT_CLOCKED_IN };
		}

		// Last, to match the server: getFreeDeliveries applies the city-wide rules
		// above and rankForOrder only then checks who already let this order time
		// out. A rider who fails both is reported by the city-wide reason there,
		// so it has to be reported by the city-wide reason here.
		if (excludeDriverIds.some(id => id === driver.id)) {
			return { ...base, eligible: false, reason: SKIP_REASONS.ALREADY_IGNORED };
		}

		return { ...base, eligible: true, reason: null };
	});

	return { verdicts, eligible: verdicts.filter(v => v.eligible) };
};

// util/auto-assign-order.js — rankForOrder. The per-order half: the shop's
// whitelist, the distance ceiling, and the soft ranking. `takenDriverIds` is how
// the sweep stops one driver being handed two orders in the same pass.
const rankForOrder = ({
	eligible,
	order,
	shop,
	shops,
	drivers,
	state,
	settings,
	assignedInPass = {},
	minFreeOrders = null,
	// How many orders are waiting for a driver at this moment, this one included.
	// null = do not weigh it in, which is what the look-ahead pass passes: an
	// order that is not ready yet is not demand.
	waitingOrders = null,
	requireBatch = false,
}) => {
	const scoring = resolveScoring(settings.autoAssignScoring);
	const shopsById = Object.fromEntries(shops.map(sh => [sh.id, sh]));
	const newBatchOrder = toBatchOrder(order, shopsById, false);
	// Clamped to at least 1, like the server: 0 in "orders together per pass"
	// means "don't batch", and 1 IS don't batch — not unlimited.
	const maxBatchSize = (() => {
		// 0 = no ceiling, which is what every city did before this setting existed
		// and is what "δεν έχει σημασία" means everywhere else on the page. Anything
		// above 0 is a real ceiling on orders in hand.
		const n = Number(scoring.maxBatchSize);
		return isFinite(n) && n > 0 ? n : Infinity;
	})();

	// Drivers with nothing on them at all, counted city-wide: this describes the
	// moment, not this order's chances. Anyone this pass has already given
	// something to is no longer free, which is what makes the figure move as a
	// sweep works through its queue.
	const freeDrivers = eligible.filter(
		c => !c.liveOrders && !(assignedInPass[c.driverId] || []).length,
	).length;

	const demand =
		waitingOrders === null || waitingOrders === undefined
			? null
			: Math.max(1, Number(waitingOrders) || 0);

	const supplyScarcity =
		demand === null
			? null
			: computeSupplyScarcity({ freeRiders: freeDrivers, waitingOrders: demand });

	const scored = eligible.map(candidate => {
		const inPass = assignedInPass[candidate.driverId] || [];
		const load = candidate.liveOrders + inPass.length;

		// Re-checked per order: the city-wide pass sees the state at the start and
		// cannot know what this same pass has already handed out.
		if (minFreeOrders !== null && load >= minFreeOrders) {
			return { ...candidate, eligible: false, reason: SKIP_REASONS.TOO_MANY_ORDERS };
		}

		if (!isAllowedByShop(shop, candidate.driverId)) {
			return { ...candidate, eligible: false, reason: SKIP_REASONS.NOT_ALLOWED_BY_SHOP };
		}

		const driver = drivers.find(d => d.id === candidate.driverId);

		// Everything this driver is already taking: what they held before the pass,
		// plus whatever this pass has given them since. Needed before the distance
		// is measured, because a carrying driver may be measured from where they
		// are heading rather than from their fix.
		const heldNow = state.ordersByDriver[candidate.driverId] || [];
		const held = [
			// A pre-existing order the scenario knows nothing about (shopId null)
			// still counts: as a placeless entry it weighs one on the load term and
			// says nothing to batching or the route — exactly how the server treats
			// an order whose address never geocoded. Filtering it out entirely made
			// a driver holding two look empty to the load criterion.
			...heldNow.map(o =>
				o.shopId === null
					? { id: o.id, carrying: !!o.carrying, shop: null, dest: null, readyAt: null }
					: toBatchOrder(
							{
								id: o.id,
								shopId: o.shopId,
								lat: o.lat,
								lng: o.lng,
								createdAtSeconds: o.createdAtSeconds,
								delayMinutes: o.delayMinutes,
							},
							shopsById,
							o.carrying,
						),
			),
			...inPass,
		]
			// An order assigned EARLIER IN THIS PASS is in both lists: `assign` puts
			// it on the driver straight away, and the sweep also tracks it in
			// assignedInPass. On the server there is no such overlap — heldBatchOrders
			// is a snapshot taken before the pass and never touched again — so the
			// duplicate is a simulator artefact. It was harmless while the ceiling
			// counted assignedInPass alone; counting what is in hand makes it count a
			// driver's single order as two.
			.filter((o, i, all) => all.findIndex(x => x.id === o.id) === i);

		// A ceiling on orders IN HAND, not on orders handed out in one pass. It
		// used to count `inPass` alone, which made it far weaker than its name —
		// a driver at the cap was skipped for the rest of that pass and eligible
		// again on the next one seconds later. See auto-assign-order.js.
		if (held.length >= maxBatchSize) {
			return { ...candidate, eligible: false, reason: SKIP_REASONS.BATCH_FULL };
		}

		// The stops this driver is already committed to. A collected order leaves
		// one — the drop. An assigned but uncollected one leaves two, because the
		// trip to that counter is still ahead of them, and a shop on the way there
		// is the cheapest pickup in the city.
		const waypoints = [];
		for (const h of held) {
			if (!h.carrying && h.shop) waypoints.push(h.shop);
			if (h.dest) waypoints.push(h.dest);
		}

		// The simulator has no GPS stream, so "position age" is a property of the
		// driver rather than of a stored fix. Same effect either way.
		const { trust: positionTrustValue } = resolveDistanceForScoring({
			positionAgeMinutes: driver?.positionAgeMinutes,
			meters: null,
			scoring,
		});

		const route = computeRouteCost({
			from: positionTrustValue > 0 && driver ? { lat: driver.lat, lng: driver.lng } : null,
			waypoints,
			to: shop ? { lat: shop.lat, lng: shop.lng } : null,
		});

		// Committed stops are facts rather than sensor readings, so they carry full
		// confidence on their own.
		const trust = route.mode === 'destinationOnly' ? 1 : positionTrustValue;
		const meters = route.meters;

		// The ceiling is about where the driver physically IS, never about the
		// marginal cost — one number cannot mean "at most 2 km away" for a free
		// driver and "at most 2 km of detour" for a busy one.
		const ceiling = Number(scoring.maxDistanceMeters);
		if (ceiling > 0 && route.rawMeters !== null && route.rawMeters > ceiling) {
			return { ...candidate, eligible: false, reason: SKIP_REASONS.TOO_FAR };
		}

		const batch = computeBatchFit({ held, newOrder: newBatchOrder, scoring });

		if (requireBatch) {
			// Nothing to join, or a match the configured scales already call bad.
			if (batch.fit === null || !(batch.fit > 0)) {
				return { ...candidate, eligible: false, reason: SKIP_REASONS.NO_BATCH_TO_JOIN };
			}
			// The match must include the ready-time comparison, which only exists
			// for an order the driver has not collected yet — otherwise this would
			// pre-assign a not-ready order with no timing check at all.
			if (!batch.parts.some(p => p.key === 'ready')) {
				return { ...candidate, eligible: false, reason: SKIP_REASONS.NO_BATCH_TO_JOIN };
			}
		}

		const scoredCandidate = scoreCandidate({
			facts: {
				idleMinutes: candidate.idleSeconds === null ? null : candidate.idleSeconds / 60,
				liveOrders: load,
				meters,
				positionTrust: trust,
				rawMeters: route.rawMeters,
				routeMode: route.mode,
				detourMeters: route.detourMeters,
				afterMeters: route.afterMeters,
				supplyScarcity,
				freeRiders: freeDrivers,
				waitingOrders: demand,
				effectiveLoad: computeEffectiveLoad({ held, scoring }),
				batchFit: batch.fit,
				batchParts: batch.parts,
				batchWithOrderId: batch.matched?.id ?? null,
				batchWithCarrying: batch.matched?.carrying ?? null,
			},
			scoring,
		});

		// The floor: applied after scoring, because it is about the result rather
		// than the rider.
		const floor = Number(scoring.minAssignScore) || 0;
		if (floor > 0 && scoredCandidate.score < floor) {
			return {
				...candidate,
				liveOrders: load,
				eligible: false,
				reason: SKIP_REASONS.SCORE_TOO_LOW,
				scored: scoredCandidate,
			};
		}

		return { ...candidate, liveOrders: load, scored: scoredCandidate };
	});

	const ranked = scored
		.filter(c => c.eligible)
		.sort((a, b) => {
			if (b.scored.score !== a.scored.score) return b.scored.score - a.scored.score;
			// Ties are the default, not an edge case: the idle term is clamped at
			// 100. Breaking on raw idleness keeps the old ordering.
			const aIdle = a.idleSeconds === null ? Infinity : a.idleSeconds;
			const bIdle = b.idleSeconds === null ? Infinity : b.idleSeconds;
			if (aIdle !== bIdle) return bIdle - aIdle;
			return `${a.driverId}`.localeCompare(`${b.driverId}`);
		});

	return {
		scored,
		ranked,
		chosen: ranked[0] || null,
		// The moment this ranking ran in — identical for every candidate of one
		// call, so it lives here and not in some candidate's facts.
		supply: {
			freeRiders: freeDrivers,
			waitingOrders: demand,
			scarcity: supplyScarcity,
			factors: supplyWeightFactors(supplyScarcity, scoring),
		},
	};
};

// Full trace of one attempt to give one order to somebody, whatever triggered
// it. Every driver appears with a verdict so the admin can see the near misses.
const decideForOrder = ({
	order,
	shop,
	shops,
	drivers,
	state,
	settings,
	atSeconds,
	trigger,
	assignedInPass = {},
	waitingOrders = null,
	requireBatch = false,
}) => {
	const excludeDriverIds = state.ignoredDriversByOrder[order.id] ?? [];

	const { verdicts, eligible } = rankDrivers({
		drivers,
		state,
		settings,
		atSeconds,
		excludeDriverIds,
	});

	const { scored, chosen, supply } = rankForOrder({
		eligible,
		order,
		shop,
		shops,
		drivers,
		state,
		settings,
		assignedInPass,
		minFreeOrders: limitOf(settings.orderAutoAssignMinFreeOrders),
		waitingOrders,
		requireBatch,
	});

	// One verdict list for the UI to read: the hard rejections from rankDrivers,
	// with the per-order ones and the score breakdowns folded in on top.
	const byId = Object.fromEntries(scored.map(s => [s.driverId, s]));
	const detailed = verdicts.map(v => byId[v.driverId] || v);

	return {
		orderId: order.id,
		trigger,
		atSeconds,
		verdicts: detailed,
		chosenDriverId: chosen?.driverId ?? null,
		chosenScore: chosen?.scored?.score ?? null,
		supply,
	};
};

const assign = ({ state, order, driverId, atSeconds }) => {
	order.driverId = driverId;
	order.assignedAtSeconds = atSeconds;
	order.assignConfirmed = false;
	state.liveOrdersByDriver[driverId] = (state.liveOrdersByDriver[driverId] ?? 0) + 1;
	// What the batching term reads: which shop each held order belongs to, and
	// whether it has been picked up yet.
	state.ordersByDriver[driverId] = [
		...(state.ordersByDriver[driverId] || []),
		{
			id: order.id,
			shopId: order.shopId,
			lat: order.lat,
			lng: order.lng,
			createdAtSeconds: order.createdAtSeconds,
			delayMinutes: order.delayMinutes,
			carrying: false,
		},
	];
};

const unassign = ({ state, order }) => {
	if (order.driverId !== null) {
		state.ordersByDriver[order.driverId] = (state.ordersByDriver[order.driverId] || []).filter(
			o => o.id !== order.id,
		);
		state.liveOrdersByDriver[order.driverId] = Math.max(
			0,
			(state.liveOrdersByDriver[order.driverId] ?? 1) - 1,
		);
	}
	order.driverId = null;
	order.assignedAtSeconds = null;
	order.assignConfirmed = false;
	order.carrying = false;
};

// controllers/shop/order/order-assign.js — autoAssignOrders.
const runSweep = ({ orders, shopsById, drivers, state, settings, atSeconds, log }) => {
	if (!settings.operating) {
		log.push({
			type: 'sweepSkipped',
			atSeconds,
			reason: ORDER_SKIP_REASONS.NOT_OPERATING,
		});
		return;
	}

	const pendingAtLeast = limitOf(settings.orderAutoAssignPendingAtLeastMinutes);

	const scoringNow = resolveScoring(settings.autoAssignScoring);
	const graceSeconds = Number(scoringNow.readyGraceSeconds) || 0;

	// How far BEFORE ready this city hands an order over on its own merit — not
	// as an addition to somebody's trip, which is what the look-ahead does. Both
	// of the settings that delay assignment relative to ready switch it off: they
	// exist to give a person or a rider first refusal, and taking an order before
	// it is ready contradicts either one outright. See order-assign.js.
	const preReadySeconds =
		graceSeconds > 0 || (pendingAtLeast !== null && pendingAtLeast > 0)
			? 0
			: Number(scoringNow.preReadyMinutes || 0) * 60;

	const withinPreReady = o => {
		const readyAt = readyAtOf(o);
		return preReadySeconds > 0 && readyAt > atSeconds && readyAt - atSeconds <= preReadySeconds;
	};

	const candidates = orders
		.filter(o => o.createdAtSeconds <= atSeconds && o.driverId === null)
		.filter(o => {
			// Not ready, but close enough that this city assigns ahead of it.
			if (withinPreReady(o)) return true;
			if (!isOrderReady(o, atSeconds)) return false;
			// Hands-off window after the order becomes ready.
			if (graceSeconds > 0 && atSeconds - readyAtOf(o) < graceSeconds) return false;
			if (pendingAtLeast === null) return true;
			// The backend floors the minutes before comparing, so an order is only
			// picked up once a whole minute past the limit has elapsed.
			return Math.floor((atSeconds - readyAtOf(o)) / 60) >= pendingAtLeast;
		})
		// Most urgent first, and urgency is time spent READY, not age — createdAt
		// only breaks ties.
		.sort((a, b) => {
			const ua = readyAtOf(a);
			const ub = readyAtOf(b);
			if (ua !== ub) return ua - ub;
			return a.createdAtSeconds - b.createdAtSeconds;
		});

	// Orders not ready yet but ready soon. Add-on only: the second pass below
	// offers them purely as an addition to a batch someone is already taking.
	const lookaheadSeconds =
		Number(resolveScoring(settings.autoAssignScoring).batchLookaheadMinutes || 0) * 60;

	const soonOrders =
		lookaheadSeconds > 0
			? orders
					.filter(o => o.createdAtSeconds <= atSeconds && o.driverId === null)
					.filter(o => {
						const readyAt = readyAtOf(o);
						return (
							readyAt > atSeconds &&
							readyAt - atSeconds <= lookaheadSeconds &&
							// Already in the main pass above, on its own merit.
							!withinPreReady(o)
						);
					})
					.sort((a, b) => readyAtOf(a) - readyAtOf(b))
			: [];

	if (!candidates.length && !soonOrders.length) {
		return;
	}

	// What this pass has already handed each driver. Not an exclusion list: a
	// driver who just took an order is exactly who a second, nearby order should
	// be compared against.
	const assignedInPass = {};

	// Best pair first, not order by order — see runAutoAssignSweep in
	// controllers/shop/order/order-assign.js for why. Urgency is folded into the
	// pair value rather than deciding the loop order, so a long wait still wins
	// but two orders that have waited about the same are paired on merit.
	const urgencyWeight = Number(scoringNow.orderUrgencyWeight) || 0;
	const remaining = [...candidates];

	while (remaining.length) {
		let best = null;

		for (const order of remaining) {
			const decision = decideForOrder({
				order,
				shop: shopsById[order.shopId],
				shops: Object.values(shopsById),
				drivers,
				state,
				settings,
				atSeconds,
				trigger: 'sweep',
				assignedInPass,
				// The queue as it stands right now, not as it stood when the pass began:
				// it shrinks with every assignment, and the count of free drivers shrinks
				// with it, so a sweep that starts out scarce eases off as it clears.
				waitingOrders: remaining.length,
			});

			if (decision.chosenDriverId === null) {
				log.push({ type: 'decision', ...decision });
				log.push({
					type: 'orderSkipped',
					atSeconds,
					orderId: order.id,
					reason: ORDER_SKIP_REASONS.NO_FREE_DRIVER,
				});
				continue;
			}

			const urgency = orderUrgency(
				{ readyAt: readyAtOf(order) * 1000, now: atSeconds * 1000 },
				scoringNow,
			);
			const pairValue = urgency * urgencyWeight + (decision.chosenScore ?? 0);

			if (!best || pairValue > best.pairValue) {
				best = { order, decision, pairValue, urgency };
			}
		}

		if (!best) break;

		remaining.splice(remaining.indexOf(best.order), 1);

		log.push({
			type: 'decision',
			...best.decision,
			// What put THIS order first in the pass — shown on the timeline and in
			// the snapshot, mirroring the `context` the server writes to its log.
			priority: {
				urgency: Math.round(best.urgency),
				urgencyWeight,
				pairValue: Math.round(best.pairValue),
				// Worth explaining only when there was actually a contest.
				competitors: remaining.length,
			},
		});

		assign({ state, order: best.order, driverId: best.decision.chosenDriverId, atSeconds });
		assignedInPass[best.decision.chosenDriverId] = [
			...(assignedInPass[best.decision.chosenDriverId] || []),
			{
				id: best.order.id,
				carrying: false,
				shop: {
					lat: shopsById[best.order.shopId]?.lat,
					lng: shopsById[best.order.shopId]?.lng,
				},
				dest: { lat: best.order.lat, lng: best.order.lng },
				readyAt: readyAtOf(best.order) * 1000,
			},
		];

		log.push({
			type: 'assigned',
			atSeconds,
			orderId: best.order.id,
			driverId: best.decision.chosenDriverId,
			trigger: 'sweep',
		});
	}

	// Second pass, after every ready order has had its pick — a look-ahead order
	// must never take a driver away from one that is waiting now.
	for (const order of soonOrders) {
		const decision = decideForOrder({
			order,
			shop: shopsById[order.shopId],
			shops: Object.values(shopsById),
			drivers,
			state,
			settings,
			atSeconds,
			trigger: 'lookahead',
			assignedInPass,
			requireBatch: true,
		});

		if (decision.chosenDriverId === null) {
			// Most orders in this window have no batch to join; logging that every
			// tick would bury the decisions that matter.
			continue;
		}

		log.push({ type: 'decision', ...decision });

		assign({ state, order, driverId: decision.chosenDriverId, atSeconds });
		assignedInPass[decision.chosenDriverId] = [
			...(assignedInPass[decision.chosenDriverId] || []),
			{
				id: order.id,
				carrying: false,
				shop: { lat: shopsById[order.shopId]?.lat, lng: shopsById[order.shopId]?.lng },
				dest: { lat: order.lat, lng: order.lng },
				readyAt: (order.createdAtSeconds + (Number(order.delayMinutes) || 0) * 60) * 1000,
			},
		];

		log.push({
			type: 'assigned',
			atSeconds,
			orderId: order.id,
			driverId: decision.chosenDriverId,
			trigger: 'lookahead',
		});
	}
};

// Replays the whole scenario second by second and returns the event log plus
// the state at every point, so the UI can scrub through time.
//
// Order of work inside one second: creations, then ready timers, then driver
// acknowledgements, then the sweep, and finally confirm timeouts. Real timers
// fire in whatever order they fire in; this is a fixed, documented choice so
// that one scenario always produces one answer.
//
// The sweep deliberately runs *before* the timeouts. On the server a revoke is
// several awaited database round-trips before the order is actually free, so a
// sweep tick landing in the same second still sees the order as assigned and
// only picks it up on its next pass. Running the timeouts first here would show
// the order changing hands a whole tick earlier than it really does.
export const runSimulation = ({ scenario, settings, horizonMinutes = 20 }) => {
	const horizonSeconds = Math.max(60, Math.round(horizonMinutes * 60));

	const shopsById = Object.fromEntries(scenario.shops.map(sh => [sh.id, sh]));
	const drivers = scenario.drivers;

	const orders = scenario.orders.map(o => ({
		...o,
		createdAtSeconds: Math.round((Number(o.createdAtMinute) || 0) * 60),
		driverId: null,
		assignedAtSeconds: null,
		assignConfirmed: false,
		carrying: false,
		collectedAtSeconds: null,
		completedAtSeconds: null,
	}));

	const state = {
		liveOrdersByDriver: Object.fromEntries(drivers.map(d => [d.id, Number(d.activeOrders) || 0])),
		// The orders a driver already had when the scenario started are counted
		// for load but carry no shop, so they never satisfy the batching term.
		// Nothing in the scenario says where they came from, and inventing a shop
		// for them would make the simulator claim something it does not know.
		ordersByDriver: Object.fromEntries(
			drivers.map(d => [
				d.id,
				Array.from({ length: Number(d.activeOrders) || 0 }, (_, i) => ({
					id: `pre-${d.id}-${i}`,
					shopId: null,
					carrying: false,
				})),
			]),
		),
		ignoredDriversByOrder: {},
		// driverId -> the second they last finished a delivery in this run. Only
		// filled when the scenario models completions; see completesAfterSeconds.
		lastCompletedAtSeconds: {},
	};

	const log = [];
	// A snapshot per second is what makes the time slider instant; a scenario is
	// a handful of orders over some tens of minutes, so this stays small.
	const snapshots = [];

	// When the check next runs. Two timers, exactly as on the server: the
	// periodic one, and an early one that an event pulls forward.
	let nextSweepAt = SWEEP_INTERVAL_SECONDS;
	let earlySweepAt = null;

	// "Something happened that could make an order assignable." Two of the
	// server's six sources cannot occur here — this simulation has no order
	// completions and no clock events — so what reaches this are orders becoming
	// ready with nobody on them, and orders taken back after a silent driver.
	const requestSweep = at => {
		if (!settings.orderAutoAssignFixedInterval) return;
		// Already coalescing this burst.
		if (earlySweepAt !== null) return;
		earlySweepAt = at + SWEEP_DEBOUNCE_SECONDS;
	};

	for (let t = 0; t <= horizonSeconds; t += 1) {
		// 1. Orders coming into existence.
		for (const order of orders) {
			if (order.createdAtSeconds !== t) continue;

			log.push({ type: 'orderCreated', atSeconds: t, orderId: order.id });
			// Creation assigns nothing on its own — it asks for a check, like every
			// other event. That matters when the city assigns ahead of ready
			// (preReadyMinutes): the order is a candidate the moment it exists, and
			// without this it would wait for the periodic check instead of five
			// seconds. See order-create.js.
			requestSweep(t);
		}

		// 2. Ready timers. order-notify.js only acts on an unassigned order.
		for (const order of orders) {
			if (order.createdAtSeconds > t) continue;
			if (readyAtOf(order) !== t) continue;

			log.push({ type: 'orderReady', atSeconds: t, orderId: order.id });

			// order-notify.js asks the check to look at the city. This is the single
			// way an order enters automatic assignment — including at creation, since
			// an order with no delay reaches here the moment it exists. The check
			// applies the hands-off window itself, so nothing is skipped here.
			if (order.driverId === null) {
				requestSweep(t);
			}
		}

		// 3. Drivers acknowledging what they were given.
		for (const order of orders) {
			if (order.driverId === null || order.assignConfirmed) continue;
			const driver = drivers.find(d => d.id === order.driverId);
			const respondsAfter =
				driver?.respondsAfterSeconds === null || driver?.respondsAfterSeconds === ''
					? null
					: Number(driver?.respondsAfterSeconds);

			if (respondsAfter === null || !isFinite(respondsAfter)) continue;
			if (t - order.assignedAtSeconds !== respondsAfter) continue;

			order.assignConfirmed = true;
			log.push({
				type: 'confirmed',
				atSeconds: t,
				orderId: order.id,
				driverId: order.driverId,
			});
		}

		// 3b. Collection. Until this existed nothing in the simulation ever became
		// `carrying`, so the rules that turn on it — destination-only batching, and
		// measuring a busy driver from where they are heading — could not be shown
		// at all. A driver collects an order once it is ready and they have had it
		// for their own pickup delay.
		for (const order of orders) {
			if (order.driverId === null || order.carrying) continue;
			if (!isOrderReady(order, t)) continue;

			const driver = drivers.find(d => d.id === order.driverId);
			const after =
				driver?.picksUpAfterSeconds === null || driver?.picksUpAfterSeconds === ''
					? null
					: Number(driver?.picksUpAfterSeconds);

			if (after === null || !isFinite(after)) continue;
			if (t - Math.max(order.assignedAtSeconds, readyAtOf(order)) !== after) continue;

			order.carrying = true;
			order.collectedAtSeconds = t;
			const heldRow = (state.ordersByDriver[order.driverId] || []).find(o => o.id === order.id);
			if (heldRow) heldRow.carrying = true;

			log.push({ type: 'collected', atSeconds: t, orderId: order.id, driverId: order.driverId });
		}

		// 3b. Deliveries finishing, and the rider becoming free again.
		//
		// OPT-IN: only a driver carrying `completesAfterSeconds` ever finishes.
		// Without it nothing here completes and a driver's idle time only ages,
		// which is what every hand-built scenario in the suite is written against —
		// they are about one placement decision, and a rider who finishes mid-run
		// would change the ranking underneath the thing being tested.
		//
		// The benchmark sets it, because over half an hour a rider really does come
		// back: the measured median from assignment to completion is 406 seconds in
		// Καλαμπάκα and 1354 in Θέρμη. Without this, every configuration that caps
		// orders in hand looks like it stops working after N orders, which is an
		// artefact of the model rather than anything the city would see.
		for (const order of orders) {
			if (order.driverId === null || !order.carrying || order.completedAtSeconds != null) continue;

			const driver = drivers.find(d => d.id === order.driverId);
			const after =
				driver?.completesAfterSeconds === null || driver?.completesAfterSeconds === undefined
					? null
					: Number(driver.completesAfterSeconds);

			if (after === null || !isFinite(after)) continue;
			if (t - order.collectedAtSeconds < after) continue;

			const driverId = order.driverId;
			order.completedAtSeconds = t;
			state.ordersByDriver[driverId] = (state.ordersByDriver[driverId] || []).filter(
				o => o.id !== order.id,
			);
			state.liveOrdersByDriver[driverId] = Math.max(
				0,
				(state.liveOrdersByDriver[driverId] ?? 1) - 1,
			);
			state.lastCompletedAtSeconds[driverId] = t;

			log.push({ type: 'completed', atSeconds: t, orderId: order.id, driverId });

			// A rider coming free is exactly the kind of event the real system asks
			// for a check on — order-lifecycle.js does this on completion.
			requestSweep(t);
		}

		// 4. The sweep — before the timeouts, see the note on ordering above.
		if (
			settings.orderAutoAssignFixedInterval &&
			t > 0 &&
			(t >= nextSweepAt || (earlySweepAt !== null && t >= earlySweepAt))
		) {
			runSweep({ orders, shopsById, drivers, state, settings, atSeconds: t, log });

			// However this pass was started, the periodic one restarts from here and
			// any pending early check is spent — order-state.js does exactly this, so
			// that an early pass never leaves a second one a moment behind it.
			earlySweepAt = null;
			nextSweepAt = t + SWEEP_INTERVAL_SECONDS;
		}

		// 5. Confirm timeouts. Only automatic assignments are watched, and every
		// assignment in this simulation is one. The window follows the city's
		// resend setting, so a scenario with more repeats holds the order longer
		// — and null means the city switched the takeback off, in which case the
		// order simply stays where it was put and this whole step is skipped.
		const confirmWindow = resolveRevokeWindowSeconds(resolveScoring(settings.autoAssignScoring));

		for (const order of confirmWindow === null ? [] : orders) {
			if (order.driverId === null || order.assignConfirmed) continue;
			if (t - order.assignedAtSeconds !== confirmWindow) continue;

			const silentDriverId = order.driverId;
			const ignored = state.ignoredDriversByOrder[order.id] ?? [];
			if (!ignored.includes(silentDriverId)) {
				ignored.push(silentDriverId);
			}
			state.ignoredDriversByOrder[order.id] = ignored;

			unassign({ state, order });

			log.push({
				type: 'revoked',
				atSeconds: t,
				orderId: order.id,
				driverId: silentDriverId,
			});

			// The case the early check matters most for: this order has already
			// spent the whole confirm window on a phone nobody was reading.
			requestSweep(t);
		}

		snapshots.push({
			atSeconds: t,
			orders: orders.map(o => ({
				id: o.id,
				exists: o.createdAtSeconds <= t,
				ready: o.createdAtSeconds <= t && isOrderReady(o, t),
				driverId: o.driverId,
				assignConfirmed: o.assignConfirmed,
				assignedAtSeconds: o.assignedAtSeconds,
				carrying: o.carrying,
			})),
			liveOrdersByDriver: { ...state.liveOrdersByDriver },
		});
	}

	return { log, snapshots, horizonSeconds };
};
